/* istanbul ignore file */
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import _cx from 'classnames/bind'
import {
  Progress,
  Grid,
  Button,
  Modal,
  utils,
  Dropdown,
  Text,
} from '@adc/ui-adc'
import { DISCLAIMER } from './constants'
import cls from './styles.scss'
import Costs from './costs'
const cx = _cx.bind(cls)

const BID_PENDING = 'Placing your bid now...'

const MatchBidConfirmationModal = ({
  isPerformingBid,
  onClose,
  onConfirm,
  purchaseProfiles = [],
  biddingStatus = null,
  currentBidAmount,
  calculations,
  currentUserBidAmount,
  ...rest
}) => {
  const dropdownOptions = useMemo(() => {
    return purchaseProfiles.map(p => ({
      disabled: p.percent_complete !== 100,
      text: (
        <div
          className={cx('dropdown-option', {
            'not-allowed': p.percent_complete !== 100,
          })}
        >
          <Text className={cx('u-mr-1')}>{p.template_name}</Text>
          <Progress
            className={cx('progress-bar')}
            percent={p.percent_complete}
            color='green'
            warning={p.percent_complete < 100}
            progress
            data-elm-id={`reo_profile_${p.offer_template_id}_progress_bar`}
          />
        </div>
      ),
      value: p.offer_template_id,
    }))
  }, [purchaseProfiles])

  const defaultPurchaseProfile = useMemo(
    () =>
      purchaseProfiles.find(option => option.percent_complete === 100)
        ?.offer_template_id || null,
    [purchaseProfiles]
  )

  const [selectedPurchaseProfile, setSelectedPurchaseProfile] = useState(
    defaultPurchaseProfile
  )

  useEffect(() => {
    setSelectedPurchaseProfile(defaultPurchaseProfile)
  }, [defaultPurchaseProfile])

  const handleOnChangePurchaseProfile = useCallback(
    (_, data) => {
      setSelectedPurchaseProfile(data?.value)
    },
    [setSelectedPurchaseProfile]
  )

  const handleOnConfirm = useCallback(() => {
    onConfirm(selectedPurchaseProfile)
  }, [selectedPurchaseProfile, onConfirm])

  return (
    <Modal
      data-elm-id='match_bid_confirmation_modal'
      mobileTitleSkin='light'
      onClose={onClose}
      size='tiny'
      title='Match Bid'
      {...rest}
    >
      <Modal.Content>
        <Text
          as='span'
          color='gray'
          content='To match winning bid, you would need to select a Purchase Profile.'
        />
        <div
          className={cx('bid-pill', 'u-mt-3', 'u-mb-3')}
          data-elm-id='amount_to_bid'
        >
          <span>Match Winning Bid:</span>
          <span>{utils.formatters.formatMoney(currentBidAmount)}</span>
        </div>
        <Costs
          currentBidAmount={currentBidAmount}
          currentUserBidAmount={currentUserBidAmount}
          calculations={calculations}
        />
        <Text
          as='span'
          size='tiny'
          color='gray'
          weight='heavy'
          children='Select Purchase Profile'
        />
        <Dropdown
          placeholder='No Profile Selected'
          options={dropdownOptions}
          value={selectedPurchaseProfile}
          onChange={handleOnChangePurchaseProfile}
          className={cx('purchase-profile-dropdown')}
          noResultsMessage='No purchase profiles available'
          fluid
          search
          selection
          closeOnChange
        />
        <div className={cx('button-container', 'u-mb-3')}>
          <div className={cx('u-mt-3', 'u-mb-1')}>
            <Button
              className={cx('create-profile-button')}
              link='/dashboard/purchase-profiles/create'
              role='navigation'
              target='_BLANK'
              rel='noreferrer'
            >
              Create a purchase profile
            </Button>
          </div>
          <Grid padded>
            <Grid.Column mobile={16} tablet={8} computer={4}>
              <div className={cx('confirm-button')}>
                <Button
                  disabled={isPerformingBid || selectedPurchaseProfile === null}
                  elmId='button_confirm'
                  isStretch
                  onClick={handleOnConfirm}
                  role='primary'
                >
                  Confirm
                </Button>
              </div>
            </Grid.Column>
            <Grid.Column mobile={16} tablet={8} computer={4}>
              <Button isStretch elmId='button_cancel' onClick={onClose}>
                Cancel
              </Button>
            </Grid.Column>
          </Grid>
        </div>
        {isPerformingBid && (
          <p className={cx('u-mb-2', 'u-mt-1')}>{BID_PENDING}</p>
        )}
        <Text
          data-elm-id='match_bid_disclaimer'
          color='gray'
          content={DISCLAIMER}
        />
      </Modal.Content>
    </Modal>
  )
}

MatchBidConfirmationModal.propTypes = {
  currentBidAmount: PropTypes.number,
  currentUserBidAmount: PropTypes.number,
  calculations: PropTypes.object,
  purchaseProfiles: PropTypes.array,
  confirmingBid: PropTypes.node,
  isPerformingBid: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  biddingStatus: PropTypes.object.isRequired,
}
export default MatchBidConfirmationModal
